import React, { useContext, useState } from "react";
import { Box, Card, CardContent } from "@mui/material";
import { ScreenContentWrapper } from "../shared/ScreenContentWrapper";
import { AddItemCard } from "./AddItemCard";
import { UserDocument } from "../../models/UserDocument";
import { DocumentItem } from "./DocumentItem";
import FormsFilesHeader from "views/formsAndFiles/FormsFilesHeader";
import { UserDocumentDialog } from "./UserDocumentDialog";
import { Loading } from "views/shared/Loading";
import { FileCabinetContext } from "FileCabinetContextWrapper";

interface IProps {
  header?: JSX.Element,
  wrapContent?: boolean,
  includeOnlyDocumentTypeIds?: number[],
}

export const FileCabinetScreen = ({header, includeOnlyDocumentTypeIds, wrapContent = true}: IProps): JSX.Element => {
  const { docs, docSrcs, loadingDocs, loadingSrcs, refreshDocs } = useContext(FileCabinetContext);
  const [activeDoc, setActiveDoc] = useState<UserDocument>()
  const [activeDocSrc, setActiveDocSrc] = useState<string>()
  const includeOnlyDocumentTypeIdsMap:{[key:number]:boolean} = {}
  includeOnlyDocumentTypeIds?.map(docTypeId => {
    includeOnlyDocumentTypeIdsMap[docTypeId] = true
  })

  function getDefaultActiveDoc() : UserDocument {
    const initialDoc = new UserDocument()
    initialDoc.documentTypeId = includeOnlyDocumentTypeIds && includeOnlyDocumentTypeIds.length > 0 ? includeOnlyDocumentTypeIds[0] : 1
    return initialDoc
  }


  const renderDocs = (): JSX.Element[] | JSX.Element => {
    if (!docs) {
      return <></>
    }

    return docs.map((doc: UserDocument, index: number) => {
      let docSrc = "";
      if (docSrcs && docSrcs[index]) {
        docSrc = docSrcs[index]
      }

      // If there are type ids that we care about, look in the map to see if this doc is in the type id map
      // Don't get confused and try to use the map in both parts of this && statement :)
      if(includeOnlyDocumentTypeIds && !includeOnlyDocumentTypeIdsMap[doc.documentTypeId || -1]) {
        return <></>
      }

      return (
        <Card
          sx={{
            my: 2,
            mr: 1,
            width: {
              sm: 197,
              xs: "100%",
            },
            transition: "box-shadow 0.1s ease-in-out",
            ":hover": {
              boxShadow: 10, // theme.shadows[20]
            },
          }}
          key={`${doc.version?.id}`}
        >
          <CardContent
            sx={{
              p: 0,
              "&:last-child": {
                pb: 0,
              },
            }}
          >
            <DocumentItem
              document={doc}
              documentSrc={docSrc}
              onClick={() => {
                setActiveDoc(doc);
                setActiveDocSrc(docSrc);
              }}
            />
          </CardContent>
        </Card>
      );
    });
  };

  const withWrapper = (child: JSX.Element): JSX.Element => {
    if(wrapContent) {
      return <ScreenContentWrapper pageSuffix="File cabinet" hideImage={true}>
        {child}
      </ScreenContentWrapper>
    }

    return child
  }

  return (
    <>
      {header || <FormsFilesHeader
        title="My Files"
        description="We’ve created this area for you to help you stay organized.  As things change, you can re-upload your files and we’ll keep a copy of both the old and new document for you."
      />}
      {withWrapper(
        <>
          {activeDoc && (
            <UserDocumentDialog
              document={activeDoc}
              documentSrc={activeDocSrc || ""}
              open={!!activeDoc}
              includeOnlyDocumentTypeIds={includeOnlyDocumentTypeIds}
              onUpdateDoc={() => {
                refreshDocs();
                setActiveDoc(undefined);
                setActiveDocSrc(undefined);
              }}
              onComplete={() => {
                refreshDocs();
                setActiveDoc(undefined);
                setActiveDocSrc(undefined);
              }}
              onCancel={() => {
                setActiveDoc(undefined);
                setActiveDocSrc(undefined);
              }}
            />
          )}
          {!(loadingDocs || loadingSrcs) ? (
            <Box>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  paddingTop: "0",
                  marginTop: "-50px",
                }}
              >
                <AddItemCard
                  title="Upload a document"
                  onClick={() => {
                    setActiveDoc(getDefaultActiveDoc());
                  }}
                />
                {renderDocs()}
              </Box>
            </Box>
          ) : (
            <Box>
              <Loading />
            </Box>
          )}
        </>
      )}
    </>
  );
};
